import { BuddyEyes, Button, Toast, ToastStack } from "@buddy/ui";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useOrg } from "@/lib/org";
import { titleBarStyle, useDesktop, useUpdateStatus, useWorkerStatus, type BuddyBridge } from "./bridge";
import { DesktopChromeContext, useDesktopChrome, type ChromeOrg } from "./chrome-context";
import { CrashToast, WorkerControl } from "./worker-status";

// Paper 13a (mac) and 13b (Windows): inside the desktop app the web draws the title bar.
// The window is frameless; the native traffic lights or caption buttons sit on top of
// this strip, so its heights match apps/desktop/src/main/window.ts. In a plain browser
// none of this renders.

/** Wraps the whole app. Adds the title bar and desktop toasts only when `window.buddy` exists. */
export function DesktopChrome({ children }: { children: ReactNode }) {
  const bridge = useDesktop();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const [org, setOrg] = useState<ChromeOrg | null>(null);
  const [workerOpen, setWorkerOpen] = useState(false);
  const value = useMemo(() => ({ org, setOrg, workerOpen, setWorkerOpen }), [org, workerOpen]);

  // The setup window (13c) is its own small window with its own header.
  if (bridge === undefined || path === "/first-run") return children;
  return (
    <DesktopChromeContext value={value}>
      <div className="flex h-full flex-col bg-app">
        {titleBarStyle(bridge) === "mac" ? <MacTitleBar org={org} /> : <WindowsTitleBar />}
        <div className="min-h-0 flex-1">{children}</div>
      </div>
      <DesktopToasts bridge={bridge} />
    </DesktopChromeContext>
  );
}

/** Tells the title bar which org is open, from inside the signed-in frame. Renders nothing. */
export function DesktopOrg() {
  const { id, name } = useOrg();
  const setOrg = useDesktopChrome()?.setOrg;
  useEffect(() => {
    if (setOrg === undefined) return;
    setOrg({ id, name });
    return () => setOrg(null);
  }, [setOrg, id, name]);
  return null;
}

const drag = "[-webkit-app-region:drag] select-none";

function MacTitleBar({ org }: { org: ChromeOrg | null }) {
  return (
    <header className={`flex h-[38px] shrink-0 items-center justify-between bg-app px-3.5 ${drag}`}>
      {/* The native traffic lights draw here, 14px in. */}
      <div className="w-60 shrink-0" />
      <p className="min-w-0 truncate text-[13px] leading-4 text-text-2">
        {org === null ? "Buddy" : `Buddy, ${org.name}`}
      </p>
      <div className="flex w-60 shrink-0 justify-end">
        <WorkerControl />
      </div>
    </header>
  );
}

// Width of the native caption buttons: the Window Controls Overlay area when Electron
// reports it, else the 3 x 46px Windows draws at 100% scale.
const captionButtonsWidth = "calc(100vw - env(titlebar-area-x, 0px) - env(titlebar-area-width, calc(100vw - 138px)))";

function WindowsTitleBar() {
  return (
    <header className={`flex h-8 shrink-0 items-center justify-between bg-app pl-3 ${drag}`}>
      <div className="flex items-center gap-2">
        <span className="flex size-4 items-center justify-center rounded-[5px] bg-linear-to-b from-[#f7f7f7] to-light text-app">
          <BuddyEyes className="h-1.5 w-2.5" />
        </span>
        <p className="text-[12px] leading-4 tracking-normal text-text-2">Buddy</p>
      </div>
      <div className="flex items-center">
        <WorkerControl />
        <div className="h-8 shrink-0" style={{ width: captionButtonsWidth }} />
      </div>
    </header>
  );
}

/** Paper 13e: bottom right, over the page. The update toast sits above the crash toast. */
function DesktopToasts({ bridge }: { bridge: BuddyBridge }) {
  const update = useUpdateStatus();
  const worker = useWorkerStatus();
  const [installing, setInstalling] = useState(false);
  const showUpdate = update?._tag === "ready";
  const showCrash = worker?._tag === "restarting";
  if (!showUpdate && !showCrash) return null;
  return (
    <ToastStack className="fixed bottom-[100px] right-10 z-40">
      {update?._tag === "ready" ? (
        <Toast
          title={`Buddy ${update.version} is ready`}
          description="Restart to update. Employees finish their current step first."
          action={
            <Button
              size="sm"
              disabled={installing}
              onClick={() => {
                setInstalling(true);
                bridge.updates.install().catch((cause: unknown) => {
                  console.warn("[desktop] update install failed", cause);
                  setInstalling(false);
                });
              }}
            >
              Restart
            </Button>
          }
        />
      ) : null}
      {worker?._tag === "restarting" ? <CrashToast status={worker} /> : null}
    </ToastStack>
  );
}
